/**
 * `@core/rng` — the seeded pseudo-random generator (tech-design §2.5).
 *
 * The only randomness in the engine. Determinism is a hard requirement: every
 * breakthrough roll must be reproducible so unit tests can assert exact outcomes
 * and an exported save replays identically.
 *
 * State is just `{ seed, counter }` (§2.1). We use a **counter-addressable** PRNG:
 * the value of draw _n_ is a hash of `(seed, n)`, so advancing is O(1) (no need to
 * iterate a xoshiro register _n_ times) and the whole stream is a pure function of
 * the two stored fields. `Math.random`/`Date` are lint-banned in `core`; this is
 * the sanctioned source.
 */

/** Serializable RNG state: a seed string plus a monotonically increasing draw counter. */
export interface RngState {
  readonly seed: string;
  readonly counter: number;
}

/** A drawn value together with the advanced RNG state (the input is never mutated). */
export interface Draw<T> {
  readonly value: T;
  readonly rng: RngState;
}

export function createRng(seed: string): RngState {
  return { seed, counter: 0 };
}

/** xmur3 string hash → a well-mixed 32-bit seed integer. */
function hashSeed(seed: string): number {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i += 1) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return h >>> 0;
}

/** splitmix32 finalizer — turns a counter word into a uniform 32-bit output. */
function splitmix32(input: number): number {
  let t = (input + 0x9e3779b9) | 0;
  t = Math.imul(t ^ (t >>> 16), 0x21f0aaad);
  t = Math.imul(t ^ (t >>> 15), 0x735a2d97);
  t = t ^ (t >>> 15);
  return t >>> 0;
}

/** Raw 32-bit output for `(seed, counter)` — the addressable core of the stream. */
function wordAt(seed: string, counter: number): number {
  const mixed = hashSeed(seed) ^ Math.imul(counter | 0, 0x9e3779b9);
  return splitmix32(mixed);
}

/** Draw the next float in `[0, 1)` and advance the counter by one. */
export function next(rng: RngState): Draw<number> {
  const word = wordAt(rng.seed, rng.counter);
  return {
    value: word / 4294967296,
    rng: { seed: rng.seed, counter: rng.counter + 1 },
  };
}

/**
 * Draw an integer in `[0, maxExclusive)`. Throws for a non-positive bound so a
 * mis-sized reward pool fails loudly rather than returning a garbage index.
 */
export function nextInt(rng: RngState, maxExclusive: number): Draw<number> {
  if (!Number.isInteger(maxExclusive) || maxExclusive <= 0) {
    throw new RangeError(
      `nextInt bound must be a positive integer, got ${String(maxExclusive)}`,
    );
  }
  const { value, rng: advanced } = next(rng);
  return { value: Math.floor(value * maxExclusive), rng: advanced };
}

/** `true` with probability `chance` (clamped to `[0, 1]`), advancing the counter. */
export function chanceHit(rng: RngState, chance: number): Draw<boolean> {
  const { value, rng: advanced } = next(rng);
  const p = chance < 0 ? 0 : chance > 1 ? 1 : chance;
  return { value: value < p, rng: advanced };
}

/**
 * Weighted pick from `[{weight}]`: returns the chosen index (or `-1` for an empty
 * list), advancing the counter once. Weights must be positive.
 */
export function weightedIndex(rng: RngState, weights: readonly number[]): Draw<number> {
  const total = weights.reduce((sum, w) => sum + w, 0);
  if (weights.length === 0 || total <= 0) {
    return { value: -1, rng: next(rng).rng };
  }
  const { value, rng: advanced } = next(rng);
  let threshold = value * total;
  for (let i = 0; i < weights.length; i += 1) {
    threshold -= weights[i] ?? 0;
    if (threshold < 0) return { value: i, rng: advanced };
  }
  return { value: weights.length - 1, rng: advanced };
}
